import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Form, Input, Progress, Select, Space, Spin, Typography, message } from "antd";
import { useNavigate } from "react-router-dom";
import { SearchOutlined, DownloadOutlined, CloudDownloadOutlined } from "@ant-design/icons";
import axios from "axios";
import { getSubscriptionStatus } from "../api_v2/subscription";
import { getToken } from "../api_v2/client";
import {
  isSafeSocialDownloadUrl,
  submitAndPollSocialDownload,
  validateSocialDownloadUrl,
} from "../api_v2/socialDownloader";
import "./SocialDownloaderPage.css";

const { Title, Paragraph, Text } = Typography;

type VideoQuality = "best" | "1080" | "720" | "480" | "360";

interface UserState {
  id: number;
  first_name?: string;
  last_name?: string;
  phone?: string;
}

interface DownloadResult {
  source: string;
  file_name: string;
  file_url: string;
  file_size_bytes?: number;
}

function bytesToReadable(bytes?: number): string {
  if (!bytes || bytes <= 0) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[unitIndex]}`;
}

function SocialDownloaderPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [user, setUser] = useState<UserState | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [probing, setProbing] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState("");
  const [pendingMessage, setPendingMessage] = useState("");
  const [result, setResult] = useState<DownloadResult | null>(null);
  const [qualityOptions, setQualityOptions] = useState<Array<{ value: VideoQuality; label: string }>>([
    { value: "best", label: "Лучшее доступное" },
  ]);
  const [qualityReady, setQualityReady] = useState(false);
  const probeControllerRef = useRef<AbortController | null>(null);
  const submitControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!getToken()) {
      message.warning("Пользователь не найден. Выполните вход.");
      navigate("/register", { replace: true });
      setLoadingStatus(false);
      return undefined;
    }

    let cancelled = false;
    getSubscriptionStatus()
      .then((data) => {
        if (cancelled) return;
        const active = data?.success && data?.subscription_status === "active";
        if (!active) {
          message.warning("Страница доступна только при активной подписке");
          navigate("/subscription", { replace: true });
          return;
        }
        const bearerUser = data?.user;
        if (!bearerUser?.id) {
          message.warning("Пользователь не найден. Выполните вход.");
          navigate("/register", { replace: true });
          return;
        }
        setUser(bearerUser);
      })
      .catch((error) => {
        if (cancelled) return;
        if (error?.response?.status === 401) {
          navigate("/register", { replace: true });
          return;
        }
        message.error("Не удалось проверить подписку");
        navigate("/subscription", { replace: true });
      })
      .finally(() => {
        if (!cancelled) setLoadingStatus(false);
      });

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => () => {
    probeControllerRef.current?.abort();
    submitControllerRef.current?.abort();
  }, []);

  const userLabel = useMemo(() => {
    if (!user) return "";
    return [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  }, [user]);

  const probeQualities = async () => {
    if (!user) return;
    probeControllerRef.current?.abort();
    const controller = new AbortController();
    probeControllerRef.current = controller;
    try {
      const values = await form.validateFields(["url"]);
      setProbing(true);
      setQualityReady(false);
      setResult(null);
      setPendingMessage("");

      const data = await validateSocialDownloadUrl(
        String(values.url || "").trim(),
        controller.signal
      );

      const labels: Record<string, string> = {
        best: "Лучшее доступное",
        "1080": "1080p",
        "720": "720p",
        "480": "480p",
        "360": "360p",
      };
      const opts = data.qualities
        .filter((q: string) => ["best", "1080", "720", "480", "360"].includes(q))
        .map((q: string) => ({ value: q as VideoQuality, label: labels[q] || q }));

      if (opts.length === 0) {
        throw new Error("Доступные качества не найдены");
      }

      setQualityOptions(opts);
      form.setFieldValue("quality", opts[0].value);
      setQualityReady(true);
      message.success("Ссылка проверена, можно выбрать качество");
    } catch (error: any) {
      if (axios.isCancel(error) || (error instanceof DOMException && error.name === "AbortError")) return;
      message.error(error?.response?.data?.message || error?.message || "Ошибка проверки ссылки");
      setQualityReady(false);
      setQualityOptions([{ value: "best", label: "Лучшее доступное" }]);
    } finally {
      if (!controller.signal.aborted) setProbing(false);
    }
  };

  const downloadFile = () => {
    if (!result?.file_url || !isSafeSocialDownloadUrl(result.file_url)) {
      message.error("Ссылка на файл отклонена как небезопасная");
      return;
    }
    const link = document.createElement("a");
    link.href = result.file_url;
    link.download = result.file_name || "video.mp4";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const submitDownload = async (values: { url: string; quality: VideoQuality }) => {
    if (!user) return;
    submitControllerRef.current?.abort();
    const controller = new AbortController();
    submitControllerRef.current = controller;

    setSubmitting(true);
    setDownloadProgress(6);
    setProgressLabel("Подготавливаем запрос...");
    setResult(null);
    setPendingMessage("");

    try {
      const prepared = await submitAndPollSocialDownload(values.url.trim(), values.quality, {
        signal: controller.signal,
        onStatus: (job, pollNumber) => {
          if (controller.signal.aborted) return;
          setProgressLabel(job.status === "processing" ? "Файл подготавливается…" : "Задание ожидает обработки…");
          setDownloadProgress(Math.min(90, 12 + pollNumber * 14));
        },
      });
      if (prepared.state === "pending") {
        setPendingMessage(
          prepared.executionEnabled
            ? "Файл продолжает готовиться в фоне. Повторите проверку позже."
            : "Обработчик загрузок пока безопасно выключен на новом backend. Задание сохранено, но файл ещё не создаётся."
        );
        message.info("Задание сохранено в очереди");
        return;
      }

      setProgressLabel("Файл готов...");
      setDownloadProgress(100);
      setResult(prepared.result);
      message.success("Файл готов к скачиванию");
    } catch (error: any) {
      if (axios.isCancel(error) || (error instanceof DOMException && error.name === "AbortError")) return;
      message.error(error?.response?.data?.message || error?.message || "Не удалось подготовить видео");
      setProgressLabel("Ошибка");
    } finally {
      if (!controller.signal.aborted) {
        setSubmitting(false);
        window.setTimeout(() => {
          setDownloadProgress(0);
          setProgressLabel("");
        }, 1200);
      }
    }
  };

  if (loadingStatus) {
    return (
      <div className="social-downloader-page social-downloader-page--loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="social-downloader-page">
      <Card className="social-downloader-card">
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
          <div>
            <Title level={2} style={{ marginBottom: 8, textAlign: "center" }}>
              Скачать видео
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 0, textAlign: "center" }}>
              Вставьте ссылку на видео из YouTube или Instagram и получите прямой файл для скачивания.
            </Paragraph>
            {userLabel && (
              <Text type="secondary" className="social-downloader-user" style={{ textAlign: "center" }}>
                Пользователь: {userLabel}
              </Text>
            )}
          </div>

          <Form
            form={form}
            layout="vertical"
            initialValues={{ quality: "best" as VideoQuality }}
            onFinish={submitDownload}
            onValuesChange={(changed) => {
              if (changed.url !== undefined) {
                setQualityReady(false);
                setQualityOptions([{ value: "best", label: "Лучшее доступное" }]);
                form.setFieldValue("quality", "best");
                setResult(null);
                setPendingMessage("");
              }
            }}
          >
            <Form.Item
              label="Ссылка на видео"
              name="url"
              rules={[
                { required: true, message: "Вставьте ссылку" },
                { type: "url", message: "Введите корректный URL" },
              ]}
            >
              <Input placeholder="https://youtube.com/... или https://instagram.com/..." />
            </Form.Item>

            <Form.Item label="Шаг 1: проверьте ссылку">
              <Button icon={<SearchOutlined />} onClick={probeQualities} loading={probing} block>
                Проверить ссылку
              </Button>
            </Form.Item>

            {qualityReady && (
              <Form.Item label="Шаг 2: выберите качество" name="quality">
                <Select options={qualityOptions} style={{ width: "100%" }} />
              </Form.Item>
            )}
            <div className="social-downloader-actions">
              <Button icon={<DownloadOutlined />} type="primary" htmlType="submit" loading={submitting} disabled={!qualityReady}>
                Подготовка к скачиванию
              </Button>
            </div>
          </Form>

          {submitting && (
            <Card type="inner">
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <Typography.Text type="secondary">
                  {progressLabel || "Скачиваем видео..."}
                </Typography.Text>
                <Progress percent={downloadProgress} status={downloadProgress >= 100 ? "success" : "active"} />
              </Space>
            </Card>
          )}

          {pendingMessage && (
            <Card type="inner">
              <Text type="secondary">{pendingMessage}</Text>
            </Card>
          )}

          {result && (
            <Card type="inner" title="Ваш файл готов">
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <div>
                  <Text strong>Источник:</Text> <Text>{result.source}</Text>
                </div>
                <div>
                  <Text strong>Имя файла:</Text> <Text ellipsis>{result.file_name}</Text>
                </div>
                <div>
                  <Text strong>Размер:</Text> <Text>{bytesToReadable(result.file_size_bytes)}</Text>
                </div>
                <Button block type="primary" icon={<CloudDownloadOutlined />} onClick={downloadFile} style={{ marginTop: 8 }}>
                  Скачать файл
                </Button>
              </Space>
            </Card>
          )}
        </Space>
      </Card>
    </div>
  );
}

export default SocialDownloaderPage;
